import { computed, Injectable, signal } from '@angular/core';

/**
 * Interface for clipboard booking info
 */
export interface ClipboardBookingInfo {
  readonly id: number;
  readonly reservationCode: string;
  readonly guestName: string;
  readonly startDate?: string;
  readonly endDate?: string;
}

/**
 * Interface for checkin clipboard data
 */
export interface CheckinClipboard {
  readonly checkinIri: string;
  readonly sourceBooking: ClipboardBookingInfo;
  readonly copiedAt: Date;
}

/**
 * Service for managing checkin copy-paste clipboard
 * Session-only, no localStorage persistence
 */
@Injectable({
  providedIn: 'root'
})
export class CheckinClipboardService {
  private readonly clipboardState = signal<CheckinClipboard | null>(null);

  /**
   * Computed signal indicating if clipboard has data
   */
  readonly hasCheckinInClipboard = computed(() => this.clipboardState() !== null);

  /**
   * Get current clipboard data
   */
  readonly clipboard = computed(() => this.clipboardState());

  /**
   * Copy a checkin to clipboard
   * @param checkinIri Checkin IRI to copy
   * @param sourceBooking Source booking information
   */
  copy(checkinIri: string, sourceBooking: ClipboardBookingInfo): void {
    this.clipboardState.set({
      checkinIri,
      sourceBooking,
      copiedAt: new Date()
    });
  }

  /**
   * Get clipboard data for pasting
   * @returns Current clipboard data or null
   */
  get(): CheckinClipboard | null {
    return this.clipboardState();
  }

  /**
   * Clear clipboard
   */
  clear(): void {
    this.clipboardState.set(null);
  }
}
